import type { Address, SendingMethod } from './common';

export type SenderDetails = {
  company_name?: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone_prefix: string;
  phone_number: string;
  /** ISO 3166-2 GB region (GB-ENG/SCT/WLS/GBN/NIR); required when country_code is GB. */
  subdivision_code?: string | null;
} & Address;

export type FreeShippingCalcMode = 'before_discounts' | 'after_discounts';
export type FreeShippingApplyMethod = 'carrier' | 'discount_function';
export type PointSelectionMode = 'basic' | 'thank_you' | 'checkout_ext';

export function requiresMapApiKeys(mode: PointSelectionMode | null | undefined): boolean {
  return mode === 'thank_you' || mode === 'checkout_ext';
}

export type MerchantSettings = {
  sender_details?: Partial<SenderDetails> | null;
  default_label_format?: string | null;
  /** Applied per shipment under the destination-resolved insurance currency; `merchant.insurance_currency` is only the indicative home-market label. */
  default_insurance_value?: number | null;
  /** Default sending (dispatch) method for new shipments; null = automatic (courier-first). */
  default_sending_method?: SendingMethod | null;
  /**
   * Default package size preselected on shipment creation: a predefined
   * template slug or `custom:{id}`; null falls back to the predefined default.
   */
  default_package_key?: string | null;
  geocoding_api_key?: string | null;
  google_maps_api_key?: string | null;
  pick_pack_time_days?: number | null;
  transit_time_days?: number | null;
  point_selection_mode?: PointSelectionMode | null;
  point_search_enabled?: boolean | null;
  free_shipping_calc_mode?: FreeShippingCalcMode | null;
  free_shipping_apply_method?: FreeShippingApplyMethod | null;
  point_edit_cutoff_enabled?: boolean | null;
  point_edit_cutoff_minutes?: number | null;
  point_reminder_enabled?: boolean | null;
  point_reminder_delay_minutes?: number | null;
  point_auto_select_enabled?: boolean | null;
  point_auto_select_radius_km?: number | null;
  inpost_geowidget_token?: string | null;
  eori_number?: string | null;
  /** Replace the order's shipping address with the selected pickup point's address (original kept on the order). */
  point_address_replace_enabled?: boolean | null;
  /** Test mode: show InPost rates at checkout only to buyers whose shipping last name is "Test". */
  test_mode_enabled?: boolean | null;
  /** Mark the Shopify order as paid when a cash-on-delivery shipment is delivered. */
  cod_mark_paid_enabled?: boolean | null;
  /** Mark the Shopify order fulfilled (tracking attached) when an InPost shipment is created. */
  fulfillment_sync_enabled?: boolean | null;
  /** Send Shopify's shipping-confirmation email (with the tracking link) on the first fulfillment. */
  fulfillment_notify_customer?: boolean | null;
};

export type MerchantInfo = {
  id: number;
  shop_domain: string;
  shopify_plan: string | null;
  is_shopify_plus: boolean;
  /**
   * Carrier-service (calculated shipping) eligibility, confirmed by the actual
   * Shopify carrierServiceCreate result. `false` hard-locks the admin (the plan
   * can't show InPost rates at checkout); `null` = not yet determined.
   */
  carrier_service_eligible: boolean | null;
  shop_currency: string | null;
  /**
   * Currency the insurance VAS must be charged in, fixed per origin market
   * (shop billing country) by InPost independently of the shop currency: PLN
   * for PL, EUR elsewhere. Shipment-creation requests must echo it back.
   * Nullable for deploy skew only (a pre-feature backend omits the field);
   * the current backend always sends a string.
   */
  insurance_currency: string | null;
  has_inpost_connection: boolean;
  installed_at: string | null;
};

export type SessionUser = {
  id: number;
  name: string;
  email: string;
  is_shop_access: boolean;
  avatar_url: string | null;
};
export type SessionAccount = { id: number; name: string };

export type OnboardingStatus = {
  /** Wizard is enabled for this shop (config master switch + market gate). */
  enabled: boolean;
  /** Merchant has already finished the wizard. */
  completed: boolean;
  /** Merchant should be redirected into the wizard now. */
  required: boolean;
};

/** InPost Geowidget SDK asset URLs + config preset (app-level, env-switchable). */
export type GeowidgetConfig = {
  script_url: string;
  style_url: string;
  config: string;
};

/** Stable keys for configuration-health issues (mirrors backend ConfigIssue enum). */
export type ConfigIssueKey =
  | 'inpost_not_connected'
  | 'no_active_delivery_method'
  | 'sender_details_incomplete'
  | 'geowidget_token_missing'
  | 'test_mode_enabled';

/**
 * Configuration-health buckets for the admin banner. `problems` are blocking
 * (the app can't work until fixed); `warnings` are advisory. The backend owns the
 * checks - the frontend only renders these keys (it never decides what's wrong).
 */
export type ConfigHealth = {
  problems: ConfigIssueKey[];
  warnings: ConfigIssueKey[];
};

export type MeResponse = {
  user: SessionUser | null;
  account: SessionAccount | null;
  merchant: MerchantInfo;
  settings: MerchantSettings;
  geowidget: GeowidgetConfig;
  onboarding: OnboardingStatus;
  config_health: ConfigHealth;
};

export type UpdateMerchantSettingsPayload = { settings: Partial<MerchantSettings> };
export type UpdateMerchantSettingsResponse = { updated: string[] };

export type ValidateGeocodingApiKeyPayload = { key: string };
export type GeocodingApiName = 'geocoding' | 'places';
export type GeocodingApiProbeResult = {
  valid: boolean;
  status: string;
  error_message: string | null;
};
export type ValidateGeocodingApiKeyResponse = {
  valid: boolean;
  apis: Record<GeocodingApiName, GeocodingApiProbeResult>;
};
